import { imagePathResolver, ROUTES } from '@shared/utils';
import type { useTranslations } from 'next-intl';
import type { OfferBundlePromotion } from '../../../types';

type MapOfferArgs = {
  item: OfferBundlePromotion;
  t: ReturnType<typeof useTranslations>;
  baseImgUrl: string;
  isPreviousOffer?: boolean;
};

/**
 * Maps the offer status to the corresponding string value.
 *
 * @param {string} status - The status of the offer from backend.
 * @returns {string} The corresponding string value for the offer status.
 */
function mapOfferStatus(status: string): 'active' | 'completed' | 'expired' | 'pending' {
  const statusLower = status?.toLowerCase();
  if (statusLower === 'claimed' || statusLower === 'completed') return 'completed';
  if (statusLower === 'unlocked' || statusLower === 'active') return 'active';
  if (statusLower === 'expired') return 'expired';
  return 'pending';
}

/**
 * Maps an offer item to the display format required by the promotions list.
 *
 * @param {OfferBundlePromotion} args.item - The offer item to map.
 * @returns {object} The mapped offer object for display.
 */

function mapOffer({ item, t, baseImgUrl, isPreviousOffer = false }: MapOfferArgs) {
  const { id, title, description, status, termsAndConditions, bundleCode, iterationNr, validTo, image, callToAction, promotionType, verticals, callout } = item;
  const offerStatus = mapOfferStatus(status);
  const isAppExclusive = verticals?.includes('APP_EXCLUSIVE');
  const cta = isPreviousOffer ? t('cta.more_info') : callToAction?.text || t('cta.more_info');
  const ctaHref = isPreviousOffer ? ROUTES.OFFER_BUNDLE(bundleCode, iterationNr) : callToAction?.link || ROUTES.OFFER_BUNDLE(bundleCode, iterationNr);

  return {
    id,
    title,
    description,
    cta,
    ctaHref,
    legalText: termsAndConditions,
    expirationDate: validTo,
    status: offerStatus,
    bundleCode,
    iterationNr,
    image: { src: imagePathResolver(image?.link || '/images/casino-headline.jpg', baseImgUrl), alt: image?.altText || title, quality: promotionType === 'BUNDLE' ? 95 : 75 },
    callout: isAppExclusive ? t('app_exclusive') : callout,
    verticals,
  };
}

export { mapOffer, mapOfferStatus };
